import { injectable } from 'inversify';
import { StoredNotification } from '../model/StoredNotification';
import { Toast } from '../model/Toast';

import { LayoutableWindow, LayoutableWindowState } from './LayoutableWindow';

export class StickyStack {
  /**
   * Toast for this stack type
   *
   */
  public toast?: Toast;

  /**
   * Queue of notifications to be displayed
   */
  public notifications: StoredNotification[] = [];

  public addToast(toast: Toast): void {
    const [head] = this.notifications;
    if (!head) {
      throw new Error('No notifications in sticky stack');
    }
    this.toast = toast;
    this.toast.notification = head;
  }

  public update(notifications: StoredNotification[]): void {
    this.notifications = notifications;
    if (this.toast) {
      if (notifications.length > 0) {
        const mostRecent = notifications[notifications.length - 1];
        this.toast.notification = mostRecent;
      } else {
        // Close the toast and the indicator
        this.toast.setState(LayoutableWindowState.TRANSITION_OUT);
      }
    }
  }
}

/**
 * Specialised data structure for tracking Toast objects. Holds all active toasts, both visible and queued.
 *
 * Ensures ID uniqueness and correct toast lifecycle.
 */
@injectable()
export class LayoutStack {
  private static readonly CAPACITY: number = 20;
  private readonly _items: LayoutableWindow[];
  private readonly _queue: LayoutableWindow[];
  private _height: number;

  // Toast and queue of notifications
  public readonly stickyStack = new StickyStack();

  constructor() {
    this._items = [];
    this._queue = [];
    this._height = 0;
  }

  /**
   * List of toasts currently visible to the user.
   *
   * Includes toasts that are currently transitioning in/out.
   *
   * Sticky toasts will always be on top in the following order:
   * FAILURE > WARNING > SUCCESS > NONE
   */
  public get items(): ReadonlyArray<LayoutableWindow> {
    let items: LayoutableWindow[] = this._items.filter((x) => !x.isSticky);

    if (this.stickyStack.toast) {
      items = [this.stickyStack.toast, ...items];
    }

    return items;
  }

  /**
   * List of toasts waiting to be displayed.
   *
   * Only used when there are more toasts than can fit on-screen,
   * and for a brief period when toasts are being initialised.
   */
  public get queue(): ReadonlyArray<LayoutableWindow> {
    return this._queue;
  }

  /**
   * The combined height of all toasts in `items`. Includes padding between toasts.
   *
   * NOTE: The height is always calculated externally. Set the height using `updateHeight`.
   */
  public get height(): number {
    return this._height;
  }

  /**
   * A flag that indidicates the stack capacity isn't exceeded.
   *
   */
  public get hasCapacity(): boolean {
    return this._queue.length + this._items.length < LayoutStack.CAPACITY;
  }

  public clear(): void {
    this._items.length = 0;
    this._queue.length = 0;
    this._height = 0;
  }

  public getToast(id: string): LayoutableWindow | null {
    const predicate = (toast: LayoutableWindow) => toast.id === id;
    const item = this._items.find(predicate) || this._queue.find(predicate) || null;
    return item;
  }

  public existsWithinStack(id: string): boolean {
    return this._items.findIndex((toast) => toast.id === id) >= 0;
  }

  public existsWithinQueue(id: string): boolean {
    return this._queue.findIndex((toast) => toast.id === id) >= 0;
  }

  public addToQueue(toast: LayoutableWindow): void {
    const existing = this.getToast(toast.id);

    if (existing === null) {
      this._queue.push(toast);
    } else if (existing !== toast) {
      console.warn(
        `Cannot add toast to queue, stack/queue already contains a toast with the ID ${toast.id}`
      );
    } else {
      console.warn(
        `Cannot add toast to queue, stack/queue already contains this toast instance (ID: ${toast.id})`
      );
    }
  }

  public moveToStack(toast: LayoutableWindow): boolean {
    const index = this._queue.findIndex((t) => t.id === toast.id);

    if (index >= 0) {
      this._queue.splice(index, 1);
      this._items.push(toast);

      return true;
    } else {
      console.warn(`Cannot move item from queue to stack, no toast with id ${toast.id} in queue`);

      return false;
    }
  }

  public remove(win: LayoutableWindow): boolean {
    const id = win.id;
    const itemsIndex = this._items.findIndex((item) => item.id === id);
    const queueIndex = this._queue.findIndex((queue) => queue.id === id);

    if (win.isSticky) {
      this.stickyStack.toast = undefined;
    }

    if (itemsIndex >= 0) {
      this._items.splice(itemsIndex, 1);
    }
    if (queueIndex >= 0) {
      this._queue.splice(queueIndex, 1);
    }

    return itemsIndex >= 0 || queueIndex >= 0;
  }

  public updateHeight(height: number): void {
    this._height = height;
  }
}
